import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, map, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { PaginatedResponse } from 'src/app/interfaces/paginateResponse.interface';
import { ApprenantFode } from 'src/app/model/apprenantFode.model';
import { CertificationView, EmailCertificationResponse, ModuleType } from 'src/app/model/email-certification.model';
import { BreadcrumbItem } from 'src/app/modules/shared/components/breadcrumb/breadcrumb.component';
import { TableUtil } from 'src/app/utils/TableExport/TableUtili';
import { UtilisateursService } from '../../services/utilisateurs.service';
import { SpinnerServiceService } from 'src/app/modules/shared/serviceSpinner/spinner-service.service';

type StudentViewMode = | 'ALL' | 'FEMALE' | 'MALE' | 'CERTIFIED' | 'ENHANCED' | 'INSERES' | 'CERTIFICATE';


@Component({
  selector: 'app-apprenantbenin',
  templateUrl: './apprenantbenin.component.html',
  styleUrls: ['./apprenantbenin.component.css']
})
export class ApprenantbeninComponent implements OnInit, OnDestroy {
  certifcateOption: any[] = [];
  selectedCertificateId: string | null = '-1';

  allStudentsPage: ApprenantFode[] = [];
  students: ApprenantFode[] = [];
  dataSource = new MatTableDataSource<ApprenantFode>();

  certificationData?: EmailCertificationResponse;
  certificationRows: CertificationView[] = [];

  totalElements = 0;
  pageSize = 10;
  currentPage = 0;
  pageSizeOptions: number[] = [10, 20, 30, 50, 100];

  // NEW: terme de recherche envoyé au backend
  searchTerm = '';
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  viewMode: StudentViewMode = 'ALL';

  popupVisible = false;
  ligneDetails: any;

  breadcrumbItems: BreadcrumbItem[] = [
    { label: 'FORMATION' },
    { label: 'APPRENANTS', url: '/dashboard/users/mesapprenants' },
  ];

  displayedColumns: string[] = ['name', 'sexe', 'email', 'action'];
  iuf: any;
  user: any | null;
  role!: any;
  isBeninGestionnaire!: any;

  constructor(
    private utilisateurService: UtilisateursService,
    private spinnerService: SpinnerServiceService
  ) { }

  ngOnInit(): void {
    this.user = sessionStorage.getItem('user');
    this.iuf = JSON.parse(this.user).iuf;
    this.role = JSON.parse(this.user)?.roles;
    if (this.role.length > 0) {
      this.role.forEach((element: { id: number; roleName: any }) => {
        switch (element.roleName) {
          case 'gestionnaire-fcc-benin': {
            this.isBeninGestionnaire = true;
            break;
          }
        }
      });
    }

    // Debounce de la recherche : on attend que l'utilisateur arrête de taper
    this.searchSubject
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((value) => {
        this.searchTerm = value;
        this.currentPage = 0;
        if (this.paginator) {
          this.paginator.pageIndex = 0;
        }
        this.loadStudents();
      });

    this.loadCertificates();
    this.loadStudents();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.paginator.page.subscribe((event: PageEvent) => {
      this.currentPage = event.pageIndex;
      this.pageSize = event.pageSize;
      this.loadStudents();
    });
  }

  loadStudents(): void {
    this.spinnerService.showSpinner();
    let request$;
    let gender: string | undefined;
    let certified: boolean | undefined;
    let enhanced: boolean | undefined;
    let employed: boolean | undefined;
    let certificateId: number | undefined;

    switch (this.viewMode) {
      case 'FEMALE':
        gender = 'Femme';
        break;
      case 'MALE':
        gender = 'Homme';
        break;
      case 'CERTIFIED':
        certified = true;
        break;
      case 'ENHANCED':
        enhanced = true;
        break;
      case 'INSERES':
        employed = true;
        break;
      case 'CERTIFICATE':
        if (this.selectedCertificateId && this.selectedCertificateId !== '-1') {
          certificateId = Number(this.selectedCertificateId);
        }
        break;
    }

    request$ = this.utilisateurService.getApprenantsBeninPaged(
      this.iuf,
      this.currentPage,
      this.pageSize,
      gender,
      certified,
      enhanced,
      employed,
      this.searchTerm || undefined,
      certificateId
    );

    request$
      .pipe(
        map((res: PaginatedResponse<ApprenantFode>) => {
          this.allStudentsPage = res.content;
          this.totalElements = res.totalElements;
          this.genderFilters();
          this.spinnerService.hideSpinner();
        })
      )
      .subscribe();
  }

  loadCertifiableCourse(email: string): void {
    this.spinnerService.showSpinner();

    this.utilisateurService.getCertifiableCourseByEmail(email)
      .subscribe({
        next: (res) => {
          const grouped = new Map<string, CertificationView>();

          res.items.forEach(item => {
            const key = `${item.idCertificate}-${item.cohort}`;

            if (!grouped.has(key)) {
              grouped.set(key, {
                idCertificate: item.idCertificate,
                nameCertificate: item.nameCertificate,
                cohort: item.cohort,
                certified: false,
                enhanced: false,
                inserted: false
              });
            }

            const row = grouped.get(key)!;

            switch (item.typeModule) {
              case ModuleType.CERTIFIED:
                row.certified = true;
                break;
              case ModuleType.ENHANCED:
                row.enhanced = true;
                break;
              case ModuleType.INSERTED:
                row.inserted = true;
                break;
            }
          });

          this.certificationRows = Array.from(grouped.values());
          this.certificationData = res;
          this.spinnerService.hideSpinner();
        },
        error: (err) => {
          console.error(err);
          this.spinnerService.hideSpinner();
        }
      });
  }

  genderFilters(): void {
    let data = [...this.allStudentsPage];
    this.students = data;
    this.dataSource = new MatTableDataSource(this.students);
  }

  loadCertificates(): void {
    const certificateCall$ = this.utilisateurService.getAllAvailableCertificatesBenin(this.iuf);
    certificateCall$.subscribe(res => this.certifcateOption = res);
  }

  onCertificateChange(): void {
    this.currentPage = 0;
    this.paginator.pageIndex = 0;

    if (!this.selectedCertificateId || this.selectedCertificateId === '-1') {
      this.viewMode = 'ALL';
    } else {
      this.viewMode = 'CERTIFICATE';
    }

    this.loadStudents();
  }

  setMode(mode: StudentViewMode): void {
    this.viewMode = mode;

    this.currentPage = 0;
    this.paginator.pageIndex = 0;

    this.loadStudents();
  }

  // CHANGED: on n'utilise plus dataSource.filter, on envoie la recherche au backend
  filterText(event: Event): void {
    const value = (event.target as HTMLInputElement)
      .value
      .trim()
      .toLowerCase();

    this.searchSubject.next(value);
  }

  openDetails(row: any): void {
    this.ligneDetails = row;
    this.popupVisible = true;
    this.loadCertifiableCourse(row.email);
  }

  closeModal(): void {
    this.popupVisible = false;
  }

  export(): void {
    TableUtil.exportArrayToExcel(this.students);
  }
}